import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  ArrowLinkIcon,
  Card,
  ContentBox,
  ContentPage,
  NoResults,
  Pagination,
  Skeleton,
  TableFilters,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import useListFilters from "@hooks/useListFilters";
import { DomainStatus, escapeHtml, formatDateTime, usePageHeader } from "@components/ow";
import { questionsLabel } from "@helpers/quizForm";

// Questionários ativos da empresa para o prestador (modelo Lista em cartões do DS
// OneWork). O pendente abre a tela de responder; o respondido não abre (o back não
// devolve ao prestador as próprias respostas).

// Múltiplo das 3 colunas da grade; a paginação só aparece com mais de uma página
const PAGE_SIZE = 30;

const LIST_HREF = "/e/quiz";
const BREADCRUMB = [{ page: "Questionários", route: LIST_HREF }];

const EMPTY = { list: [], totalRegisters: 0, totalPages: 1 };

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const Top = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  > div {
    min-width: 0;
  }
`;

// Título em até 2 linhas; o inteiro fica no title
const Name = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.N100};
  color: ${({ theme }) => theme.colors.ACTION};

  svg {
    width: 14px;
  }
`;

const Pager = styled.div`
  margin-top: 20px;
`;

function QuizCardsSkeleton() {
  return (
    <Grid aria-busy="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <Skeleton $width="65%" $height="20px" $noFlex />
          <Skeleton $width="30%" $height="14px" $noFlex />
          <Skeleton $width="45%" $height="14px" $noFlex />
          <Skeleton $width="25%" $height="14px" $noFlex />
        </Card>
      ))}
    </Grid>
  );
}

/** Respondido: o back conta as respostas do próprio prestador (`totalAnswers`). */
const isAnswered = (quiz) => Number(quiz.totalAnswers) > 0;

function QuizCard({ quiz }) {
  const navigate = useNavigate();
  const answered = isAnswered(quiz);
  const route = `${LIST_HREF}/${quiz.id}`;

  const body = (
    <>
      <Top>
        <div>
          <Name title={quiz.title}>
            <Text $text={escapeHtml(quiz.title || "Questionário")} $variant="h4_bold" $color="N900" />
          </Name>
          <Text $text={questionsLabel(quiz.totalQuestions)} $variant="caption_medium" $color="N400" />
        </div>
        <DomainStatus domain="questionario" value={answered ? "respondido" : "pendente"} />
      </Top>
      {quiz.created_at ? (
        <Text $text={`Criado em ${formatDateTime(quiz.created_at)}`} $variant="caption_medium" $color="N600" />
      ) : null}
      {answered ? null : (
        <Foot>
          <Text $text="Responder" $variant="paragraph_small_bold" $color="ACTION" />
          <ArrowLinkIcon />
        </Foot>
      )}
    </>
  );

  if (answered) return <Card>{body}</Card>;

  return (
    // O cartão inteiro é o link: o href abre em outra aba pelo navegador; o clique
    // comum navega sem recarregar a página
    <Card
      $action={route}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        navigate(route);
      }}>
      {body}
    </Card>
  );
}

export default function QuizEmployee() {
  const quizService = useService("quiz");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  // Só a resposta da última requisição vale (busca e troca de página seguidas)
  const lastRequest = useRef(0);

  const { storeFilters, filters, filter, setOpenFilter, handleSetFilters, handleClearFilters, hasFilter } = useListFilters(
    {},
    setPagination
  );

  usePageHeader({ title: "Questionários", breadcrumb: BREADCRUMB });

  const searchTerm = (filters.search || "").trim();

  useEffect(() => {
    fetchQuizzes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, searchTerm]);

  async function fetchQuizzes() {
    const request = ++lastRequest.current;
    setLoading(true);
    try {
      // O back devolve ao prestador só os ativos, com o totalAnswers dele
      const params = [`page=${pagination.pageIndex + 1}`, `limit=${PAGE_SIZE}`];
      if (searchTerm) params.push(`title=${encodeURIComponent(searchTerm)}`);
      const result = await quizService.list(params.join("&"));
      if (request !== lastRequest.current) return;
      if (!result?.data?.list) throw new Error(typeof result === "string" ? result : "Erro ao carregar questionários");
      setData({
        list: result.data.list,
        totalRegisters: result.data.totalRegisters ?? result.data.list.length,
        totalPages: result.data.totalPages || 1,
      });
    } catch (error) {
      if (request !== lastRequest.current) return;
      toast.error(error?.response?.data?.msg || error?.message || "Erro ao carregar questionários");
      setData(EMPTY);
    }
    setLoading(false);
  }

  let content;
  if (loading) {
    content = <QuizCardsSkeleton />;
  } else if (data.list.length === 0) {
    content = searchTerm ? (
      <NoResults notResultText="Nenhum questionário encontrado" includeText="Limpar busca" includeAction={handleClearFilters} />
    ) : (
      <NoResults notResultText="Nenhum questionário por aqui" />
    );
  } else {
    content = (
      <>
        <Grid>
          {data.list.map((quiz) => (
            <QuizCard key={quiz.id} quiz={quiz} />
          ))}
        </Grid>
        {data.totalPages > 1 ? (
          <Pager>
            <Pagination
              $pageIndex={pagination.pageIndex}
              $pageCount={data.totalPages}
              $totalRecords={data.totalRegisters}
              $pageSize={PAGE_SIZE}
              onChangePage={(pageIndex) => setPagination((prev) => ({ ...prev, pageIndex }))}
            />
          </Pager>
        ) : null}
      </>
    );
  }

  return (
    <ContentPage>
      <ContentBox>
        <TableFilters
          storeFilters={storeFilters}
          searchTerm="search"
          searchPlaceholder="Buscar pelo título"
          filter={filter}
          handleSetFilters={handleSetFilters}
          handleClearFilters={handleClearFilters}
          setStatePagination={setPagination}
          openFilter={() => setOpenFilter((prev) => !prev)}
          disableFilter
          disableExport
          hasFilter={hasFilter}
        />
        {content}
      </ContentBox>
    </ContentPage>
  );
}
